import {Component, computed, effect, inject, Input, input, OnInit, output, signal, untracked} from '@angular/core';
import {MaterialModule} from '../../../../../core/modules/material.module';
import {SharedModule} from '../../../../../core/modules/shared.module';
import {applyEach, disabled, form, FormField, min, minLength, required, validate} from '@angular/forms/signals';
import {isEqual} from 'lodash';
import {EventsService} from '../../../../../core/services/events.service';
import {FuseConfigService} from '../../../../../core/services/config.service';
import {MapComponent} from '../../../../../core/components/map/map.component';
import {CompetencyService} from '../../../../../core/services/competency.service';
import {NgOptimizedImage} from '@angular/common';
import {CropperComponent} from '../../../../../core/components/cropper/cropper';
import {MatChipInputEvent} from '@angular/material/chips';
import {DocumentComponent} from '../../../../../core/components/document/document.component';
import {QuillModule} from 'ngx-quill';
import {PlatformsService} from '../../../../../core/services/platforms.service';
import {GuestEventComponent} from '../../../events/guestEventModal/guestEvent.component';
import {MatDialog} from '@angular/material/dialog';
import {NgxSpinnerService} from 'ngx-spinner';
import {ForumTalkService} from '../../../../../core/services/forumTalk.service';
import {ChatSettingComponent} from '../../../../../core/components/chat-setting/chat-setting.component';
import {CertificateTemplateService} from '../../../../../core/services/certificateTemplate.service';
import {CertificateSetting, CertificateSettingComponent} from '../../../../../core/components/certificate-setting/certificate-setting.component';

interface Dates {
  id?: number;
  eventId?: number;
  date: Date;
  startTime: Date;
  endTime: Date;
}

interface SubEvent {
  id?: number;
  eventId?: number;
  title: string;
  description: string;
  date: Date;
  fromTime: Date;
  toTime: Date;
  minParticipants: number;
  maxParticipants: number;
  picture: string;
  visibility: 'public' | 'internal';
  file: File | null;
  skills: any[] | null;
}

interface ChatSetting {
  id?: number;
  status: boolean;
  room_title: string;
  room_avatar: string;
  room_color: string;
  room_id?: string;
}

interface Event {
  id?: number;
  title: string;
  description: string;
  eventTypeId: number;
  userId: number;
  chatAdmins: number[];
  targetGroup: any[];
  eventVisibility: 'public' | 'internal' | 'admin';
  countryId: number;
  cityId: number;
  organizationId: number;
  datesType: 'oneDay' | 'longEvent' | 'fewDays' | 'ongoing';
  dates: Dates[];
  ongoingEvent: boolean;
  locationDetail: string;
  enableMap: boolean;
  location: string | null;
  hideLocation: boolean;
  enableSkills: boolean;
  skills: any[] | null;
  credits: number;
  eventLevel: any[] | null;
  needEventLevel: any[] | null;
  minParticipants: number;
  maxParticipants: number;
  reservationPossible: boolean;
  guestEvent: boolean;
  allowGroupRegistration: boolean;
  hideWaitlist: boolean;
  registrationOnRequest: boolean;
  registrationStart: Date | null;
  registrationEnd: Date | null;
  subEvents: boolean;
  subEventsData: SubEvent[] | null;
  pictureFile: File | null;
  picture: string | null;
  weblinks: any[] | null;
  files: any[] | null;
  cost: number;
  costFriend: number;
  tags: any[] | null;
  platforms: any[] | null;
  additionalForm: boolean;
  minSubEventsCount: number;
  allowSameTimeSubEvent: boolean;
  pickUp: string | null;
  conditionsOfParticipation: string | null;
  material: number;
  meals: number;
  transport: number;
  rents: number;
  miscellaneous: number;
  preparationMin: number;
  preparationMax: number;
  executionMin: number;
  executionMax: number;
  followUpMin: number;
  followUpMax: number;
  gender: 'male' | 'female' | 'diverse' | 'all';
  online: boolean;
  dateVisibleFrom: Date | null;
  dateVisibleTo: Date | null;
  releaseEvent: boolean;
  reserveCount: number;
  reserveOwn: number;
  enrolment: boolean;
  endOfReservation: Date | null;
  useOrgPlaces: boolean;
  placesForGuests: number;
  mileStone: boolean;
  guestData?: any[];
  guestIndividual?: any[];
  requiredGF?: any[];
  terms?: any;
  rules?: any;
  freeText?: any;
  titleComments?: string;
  chatSetting?: ChatSetting;
  certificateAllowed: boolean;
  certificateTemplateId?: number;
}

const createTime = (hours: number, minutes: number) => {
  const d = new Date();
  d.setHours(hours, minutes, 0, 0);
  return d;
};

const initialData: Event = {
  title: '',
  description: '',
  eventTypeId: null,
  userId: null,
  chatAdmins: [],
  targetGroup: [],
  eventVisibility: 'public',
  countryId: null,
  cityId: null,
  organizationId: null,
  datesType: 'oneDay',
  dates: [{ date: new Date(), startTime: createTime(9, 0), endTime: createTime(18, 0)}],
  ongoingEvent: false,
  locationDetail: '',
  enableMap: false,
  location: null,
  hideLocation: false,
  enableSkills: false,
  skills: null,
  credits: 0,
  eventLevel: null,
  needEventLevel: null,
  minParticipants: 0,
  maxParticipants: 0,
  reservationPossible: true,
  guestEvent: false,
  allowGroupRegistration: false,
  hideWaitlist: false,
  registrationOnRequest: false,
  registrationStart: null,
  registrationEnd: null,
  subEvents: false,
  subEventsData: null,
  picture: null,
  pictureFile: null,
  weblinks: [],
  files: [],
  cost: 0,
  costFriend: 0,
  tags: [],
  platforms: [],
  additionalForm: false,
  minSubEventsCount: 0,
  allowSameTimeSubEvent: false,
  pickUp: '',
  conditionsOfParticipation: '',
  material: 0,
  meals: 0,
  transport: 0,
  rents: 0,
  miscellaneous: 0,
  preparationMin: 0,
  preparationMax: 0,
  executionMin: 0,
  executionMax: 0,
  followUpMin: 0,
  followUpMax: 0,
  gender: 'all',
  online: false,
  dateVisibleFrom: null,
  dateVisibleTo: null,
  releaseEvent: false,
  reserveCount: 0,
  reserveOwn: 0,
  enrolment: false,
  endOfReservation: null,
  useOrgPlaces: true,
  placesForGuests: 0,
  mileStone: false,
  certificateAllowed: false,
  certificateTemplateId: null
};



@Component({
  selector: 'event-form',
  templateUrl: './event-form.component.html',
  styleUrls: ['./event-form.component.scss'],
  imports: [MaterialModule, SharedModule, FormField, MapComponent, NgOptimizedImage, CropperComponent, DocumentComponent, QuillModule, ChatSettingComponent, CertificateSettingComponent],
  providers: [EventsService, CompetencyService]
})

export class EventFormComponent implements OnInit {
  private eventService = inject(EventsService);
  private fuseConfig = inject(FuseConfigService);
  private competencyService = inject(CompetencyService);
  private platformsService = inject(PlatformsService);
  private dialog = inject(MatDialog);
  private spinner = inject(NgxSpinnerService);
  private forumTalkService = inject(ForumTalkService);
  private certificateTemplateService = inject(CertificateTemplateService);

  private datesEffectInitialized = false;

  quillConfig: any = [['bold', 'italic', 'underline', 'strike'],
    ['blockquote', 'code-block'],
    [{'list': 'ordered'}, {'list': 'bullet'}],
    [{'link': 'link'}],
    ['clean']];
  quillModules = {
    toolbar: this.quillConfig,
    clipboard: {
      matchVisual: false
    }
  };

  formResult = output<any>();
  isValid = output<any>();
  isChangedOutput = output<boolean>();
  eventData = input<any>();

  eventModel = signal<Event>(initialData);
  countries = signal<any[]>([]);
  cities = signal<any[]>([]);
  organizations = signal<any[]>([]);
  eventTypes = signal<any[]>([]);
  users = signal<any[]>([]);
  targetGroups = signal<any[]>([]);
  competencies = signal<any[]>([]);
  eventLevels = signal<any[]>([]);
  tags = signal<any[]>([]);
  platforms = signal<any[]>([]);
  certificateData = computed<CertificateSetting>(() => ({
    certificateAllowed: this.eventModel().certificateAllowed,
    certificateTemplateId: this.eventModel().certificateTemplateId
  }), {equal: isEqual});

  opennedCropper = signal<boolean>(false);

  initialLoadedData = signal<Event | null>(null);

  role = this.fuseConfig.role_Sig;
  forumChatEnabled = this.forumTalkService.forumChatEnabled;
  certificateEnabled = this.certificateTemplateService.certificateEnabled;

  chatAdminUsers = computed(() => {
    const responsibleId = this.eventModel().userId;
    return this.users().filter(user =>
      String(user.id) !== String(responsibleId) && !user.isSafeguard && !user.isSecretariat
    );
  });

  eventForm = form(this.eventModel, (event) => {
    required(event.title, {message: 'event.is_required_field'});
    // required(event.description, {message: 'event.is_required_field'});
    required(event.eventTypeId, {message: 'event.is_required_field'});
    required(event.userId, {message: 'event.is_required_field'});
    required(event.countryId, {message: 'event.is_required_field'});
    required(event.cityId, {message: 'event.is_required_field'});
    required(event.organizationId, {message: 'event.is_required_field'});
    required(event.certificateTemplateId, {
      when: ({valueOf}) => valueOf(event.certificateAllowed),
      message: 'event.is_required_field'
    });
    required(event.targetGroup, {message: 'event.is_required_field'});
    required(event.minSubEventsCount, {message: 'event.is_required_field'});
    minLength(event.targetGroup, 1, {message: 'event.is_required_field'});
    required(event.datesType, {message: 'event.is_required_field'});
    required(event.skills, {
      message: 'event.is_required_field',
      when: (ctx) => ctx.valueOf(event.enableSkills)
    });
    validate(event.skills, ({value, valueOf}) => {
      const isEnabled = valueOf(event.enableSkills);
      const skillValue = value();
      if (isEnabled && !skillValue.length) {
        return {
          kind: 'required',
          message: 'event.is_required_field'
        };
      }
      return null;
    });
    min(event.minParticipants, 0, {message: 'event.must_be_bigger_zero'});
    min(event.maxParticipants, 0, {message: 'event.must_be_bigger_zero'});
    required(event.maxParticipants, {
      message: 'event.is_required_field',
      when: (ctx) => ctx.valueOf(event.reservationPossible)
    });
    validate(event.maxParticipants, ({value, valueOf}) => {
      if (valueOf(event.reservationPossible) && value() < 1) {
        return {kind: 'min', message: 'event.must_be_bigger_zero'};
      }
      return null;
    });
    min(event.cost, 0, {message: 'event.must_be_bigger_zero'});
    min(event.costFriend, 0, {message: 'event.must_be_bigger_zero'});
    min(event.minSubEventsCount, 0, {message: 'event.must_be_bigger_zero'});
    min(event.material, 0, {message: 'event.must_be_bigger_zero'});
    min(event.meals, 0, {message: 'event.must_be_bigger_zero'});
    min(event.transport, 0, {message: 'event.must_be_bigger_zero'});
    min(event.rents, 0, {message: 'event.must_be_bigger_zero'});
    min(event.miscellaneous, 0, {message: 'event.must_be_bigger_zero'});
    min(event.executionMin, 0, {message: 'event.must_be_bigger_zero'});
    min(event.executionMax, 0, {message: 'event.must_be_bigger_zero'});
    min(event.followUpMin, 0, {message: 'event.must_be_bigger_zero'});
    min(event.followUpMax, 0, {message: 'event.must_be_bigger_zero'});
    min(event.preparationMin, 0, {message: 'event.must_be_bigger_zero'});
    min(event.preparationMax, 0, {message: 'event.must_be_bigger_zero'});
    applyEach(event.subEventsData, (subEvent) => {
      required(subEvent.title, {message: 'event.is_required_field'});
    });
    min(event.reserveCount, 0, {message: 'event.must_be_bigger_zero'});
    min(event.reserveOwn, 0, {message: 'event.must_be_bigger_zero'});
    min(event.placesForGuests, 0, {message: 'event.must_be_bigger_zero'});
    disabled(event.ongoingEvent, () => this.disableAll());
    disabled(event.datesType, () => this.disableAll());
    disabled(event.dates, () => this.disableAll());
    disabled(event.subEvents, () => this.isSubEventsDisabled());
  });

  disableAll = computed(() => !!this.eventData()?.participants);


  showCountrySelect = computed(() => this.role() === 'superAdmin');
  showCantonSelect = computed(() => this.role() === 'superAdmin' || this.role() === 'countryAdmin');
  showOrganizationSelect = computed(() => this.role() === 'superAdmin' || this.role() === 'countryAdmin' || this.role() === 'subCountryAdmin');
  showTimeAndBudget = computed(() => {
    const selectedId = this.selectedOrganizationId();
    const organization = this.organizations().find(org => org.id === selectedId);
    return organization ? !!organization.allowTimeAndBudget : false;
  });

  isChanged = computed(() => {
    const referenceData = this.initialLoadedData() || initialData;
    return !isEqual(this.eventModel(), referenceData);
  });

  selectedCountryId = computed(() => this.eventModel().countryId);
  selectedCityId = computed(() => this.eventModel().cityId);
  selectedOrganizationId = computed(() => this.eventModel().organizationId);
  selectedEventDateType = computed(() => this.eventModel().datesType);
  selectedOngoing = computed(() => this.eventModel().ongoingEvent);
  selectedEnableMap = computed(() => this.eventModel().enableMap);
  selectedEnableSkills = computed(() => this.eventModel().enableSkills);
  selectedSubEvents = computed(() => this.eventModel().subEvents);
  selectedSkills = computed(() => this.eventModel().skills);
  selectedReleaseEvent = computed(() => this.eventModel().releaseEvent);
  selectedUseOrgPlaces = computed(() => this.eventModel().useOrgPlaces);
  selectedGuestAndReleaseEvent = computed(() => this.eventModel().guestEvent && this.eventModel().releaseEvent);

  subEventCompetencies = computed(() => {
    const selectedSkillIds = this.selectedSkills();
    const allCompetencies = this.competencies();
    const skillIdsSet = new Set(selectedSkillIds);
    return allCompetencies
      .map(comp => ({
        ...comp,
        skills: comp.skills.filter(skill => skillIdsSet.has(skill.id))
      }))
      .filter(comp => comp.skills.length > 0);
  });

  allAvailableIds = computed(() =>
    this.targetGroups().flatMap(group => group.data.map(item => item.id))
  );

  isAllSelected = computed(() => {
    const selected = this.eventModel().targetGroup || [];
    const allIds = this.allAvailableIds();
    return allIds.length > 0 && allIds.every(id => selected.includes(id));
  });

  isAllPartiallySelected = computed(() => {
    const selected = this.eventModel().targetGroup || [];
    const allIds = this.allAvailableIds();
    return selected.length > 0 && selected.length < allIds.length;
  });

  isBasicValid = computed(() => this.eventForm.title().valid()
    && this.eventForm.eventTypeId().valid() && this.eventForm.userId().valid() && this.eventForm.targetGroup().valid()
    && this.eventForm.countryId().valid() && this.eventForm.cityId().valid() && this.eventForm.organizationId().valid());

  isTimeLocationValid = computed(() => this.eventForm.datesType().valid());
  isParticipantValid = computed(() => this.eventForm.maxParticipants().valid());
  isPictureValid = computed(() => this.eventForm.pictureFile().value() || this.eventForm.picture().value());
  isSubEventPictureValid = computed(() => {
    const subEvents = this.eventForm?.subEventsData()?.value();
    if (!subEvents || subEvents.length === 0) {
      return true;
    }
    return subEvents.every((sub) => sub.file || sub.picture);
  });
  isSubEventsDisabled = computed(() => {
    const type = this.eventForm.datesType().value();
    const datesCount = this.eventForm.dates().value().length;

    return type === 'fewDays' ||
      type === 'ongoing' ||
      (type === 'oneDay' && datesCount > 1);
  });

  isSubEventsTooltipDisabled = computed(() => {
    const type = this.eventForm.datesType().value();
    const datesCount = this.eventForm.dates().value().length;

    return type === 'longEvent' ||
      (type === 'oneDay' && datesCount === 1);
  });

  subEventMinDate = computed(() => {
    const type = this.eventForm.datesType().value();
    const dates = this.eventForm.dates().value();
    if (type === 'longEvent' && dates.length >= 1) {
      return dates[0].date;
    }
    return this.minDate;
  });

  subEventMaxDate = computed(() => {
    const type = this.eventForm.datesType().value();
    const dates = this.eventForm.dates().value();
    if (type === 'longEvent' && dates.length >= 2) {
      return dates[1].date;
    }
    return null;
  });

  private readonly _maxParticipants = computed(() => this.eventModel().maxParticipants);
  private readonly _reserveCount = computed(() => this.eventModel().reserveCount || 0);
  private readonly _placesForGuests = computed(() => this.eventModel().placesForGuests || 0);
  private readonly _useOrgPlaces = computed(() => this.eventModel().useOrgPlaces);
  private readonly _reserveOwn = computed(() => this.eventModel().reserveOwn);

  minDate: Date = new Date();

  constructor() {
    effect(() => {
      const responsibleId = this.eventModel().userId;
      const chatAdmins = this.eventModel().chatAdmins || [];

      if (responsibleId == null || !chatAdmins.some(id => String(id) === String(responsibleId))) {
        return;
      }

      untracked(() => {
        this.eventModel.update(event => ({
          ...event,
          chatAdmins: (event.chatAdmins || []).filter(id => String(id) !== String(responsibleId))
        }));
      });
    });

    effect(() => {
      const data = this.eventData();
      if (data) {
        untracked(() => {
          if (data.datesType === 'ongoing') {
            data.ongoingEvent = true;
          }
          this.eventModel.update(e => ({...e, ...data}));
          this.initialLoadedData.set(JSON.parse(JSON.stringify(this.eventModel())));
        });
      }
    });
    effect(() => {
      const countryId = this.selectedCountryId();
      if (countryId) {
        untracked(() => {
          this.getCantons(countryId);
          this.cleanEventSelection(true);
        });
      } else {
        this.cities.set([]);
      }
    });
    effect(() => {
      const cityId = this.selectedCityId();
      if (cityId) {
        untracked(() => {
          this.getOrganizations(cityId);
          this.cleanEventSelection();
        });
      } else {
        this.organizations.set([]);
      }
    });
    effect(() => {
      const organizationId = this.selectedOrganizationId();

      untracked(() => {
        if (organizationId) {
          this.getUserByOrganization(organizationId);
          this.getCompetenciesByOrganization(organizationId);
          this.getTagsByOrganization(organizationId);
          this.getPlatformsByOrganization(organizationId);
        } else {
          this.users.set([]);
          this.competencies.set([]);
          this.tags.set([]);
          this.platforms.set([]);
        }
      });
    });
    effect(() => {
      const result = this.eventModel();
      untracked(() => {
        this.formResult.emit(result);
        this.isValid.emit(this.isBasicValid() && this.isTimeLocationValid()
          && this.isPictureValid() && this.isSubEventPictureValid()
          && !this.opennedCropper() && this.eventForm().valid());
        this.isChangedOutput.emit(this.isChanged());
      });
    });
    effect(() => {
      const datesType = this.selectedEventDateType();
      untracked(() => {
        const currentModel = this.eventModel();
        if (!this.datesEffectInitialized) {
          this.datesEffectInitialized = true;
          if (currentModel.dates && currentModel.dates.length > 0) {
            if (currentModel.dates.some(d => d.id)) {
              return;
            }
            if (datesType === 'longEvent' && currentModel.dates.length >= 2) {
              return;
            }
            if (datesType !== 'longEvent' && currentModel.dates.length === 1) {
              return;
            }
          }
        }
        if (datesType === 'longEvent') {
          this.eventModel.update(prev => ({...prev, dates: [{date: new Date(), startTime: createTime(9, 0), endTime: createTime(18, 0)},
              {date: new Date(), startTime: createTime(9, 0), endTime: createTime(18, 0)}]}));
        } else if (datesType !== 'ongoing') {
          this.eventModel.update(prev => ({...prev, dates: [{date: new Date(), startTime: createTime(9, 0), endTime: createTime(18, 0)}]}));
        }
        if (datesType === 'ongoing' || datesType === 'fewDays') {
          this.eventModel.update(prev => ({...prev, subEvents: false, subEventsData: []}));
        }
      });
    });
    effect(() => {
      const ongoing = this.selectedOngoing();
      untracked(() => {
        const currentData = this.eventData();
        if (ongoing) {
          this.eventModel.update(prev => ({...prev, dates: [], datesType: 'ongoing', subEvents: false, subEventsData: []}));
        } else {
          if (!currentData?.ongoingEvent) {
            return;
          }
          this.eventModel.update(prev => ({...prev, datesType: 'oneDay', dates: [{date: new Date(), startTime: createTime(9, 0), endTime: createTime(18, 0)}]}));
        }
      });
    });
    effect(() => {
      const enableMap = this.selectedEnableMap();
      untracked(() => {
        const currentModel = this.eventModel();
        if (enableMap) {
          if (currentModel.hideLocation || currentModel.location) {
            return;
          }
          this.eventModel.update(prev => ({...prev, location: '', hideLocation: false}));
        } else {
          this.eventModel.update(prev => ({...prev, location: null, hideLocation: false}));
        }
      });
    });
    effect(() => {
      const enableSkills = this.selectedEnableSkills();
      untracked(() => {
        if (enableSkills) {
          this.eventModel.update(prev => ({...prev, skills: []}));
        } else {
          this.eventModel.update(prev => ({...prev, skills: null}));
        }
      });
    });
    effect(() => {
      const enableSubEvents = this.selectedSubEvents();
      untracked(() => {
        const currentModel = this.eventModel();
        if (enableSubEvents) {
          if (currentModel.subEventsData && currentModel.subEventsData.length > 0) {
            return;
          }
          this.eventModel.update(prev => ({...prev, subEventsData: new Array<SubEvent>({
              title: '',
              description: '',
              date: new Date(),
              fromTime: new Date(),
              toTime: new Date(),
              minParticipants: 0,
              maxParticipants: 0,
              picture: '',
              visibility: 'public',
              file: null,
              skills: []
            })}));
        } else {
          this.eventModel.update(prev => ({...prev, subEventsData: null}));
        }
      });
    });
    effect(() => {
      const useOrgPlaces = this.selectedUseOrgPlaces();
      untracked(() => {
        if (useOrgPlaces) {
          this.eventModel.update(prev => ({...prev, placesForGuests: 0}));
        }
      });
    });
    effect(() => {
      const maxParticipants = this._maxParticipants();
      const reserveCount = this._reserveCount();
      const placesForGuests = this._placesForGuests();
      const useOrgPlaces = this._useOrgPlaces();
      const selectedReleaseEvent = this.selectedReleaseEvent();

      untracked(() => {
        let reserveOwn = 0;

        if (maxParticipants && selectedReleaseEvent) {
          const totalMax = parseInt(maxParticipants as any, 10);
          const resCount = parseInt(reserveCount as any, 10);
          const guestPlaces = parseInt(placesForGuests as any, 10);

          if (useOrgPlaces) {
            reserveOwn = totalMax - resCount;
          } else {
            reserveOwn = totalMax - resCount - guestPlaces;
          }
        }

        if (this._reserveOwn() !== reserveOwn) {
          this.eventModel.update(prev => ({...prev, reserveOwn}));
        }
      });
    });
    effect(() => {
      const isSubEventsDisabled = this.isSubEventsDisabled();
      if (isSubEventsDisabled) {
        untracked(() => {
          const current = this.eventModel();
          if (current.subEventsData !== null || current.subEvents !== false) {
            this.eventModel.update(prev => ({...prev, subEventsData: null, subEvents: false}));
          }});
      }
    });

    effect(() => {
      const type = this.eventForm.datesType().value();
      const dates = this.eventForm.dates().value();


      untracked(() => {
        const subEvents = this.eventModel().subEventsData;
        if (!subEvents || subEvents.length === 0) {
          return;
        }
        if (type === 'oneDay' && dates.length === 1) {
          const mainDate = dates[0].date;
          const updatedSubEvents = subEvents.map(se => ({
            ...se,
            date: mainDate
          }));
          if (JSON.stringify(subEvents.map(s => s.date)) !== JSON.stringify(updatedSubEvents.map(s => s.date))) {
            this.eventModel.update(prev => ({
              ...prev,
              subEventsData: updatedSubEvents
            }));
          }
        }
      });
    });
  }

  ngOnInit() {
    this.getCountries();
    this.getEventTypes();
    this.getTargetGroups();
    this.getEventLevels();
  }

  cleanEventSelection(withCity = false) {
    this.eventModel.update(e => ({...e, platforms: [], tags: [], userId: null, skills: [], organizationId: null, ...(withCity ? {cityId: null} : {})}));
    this.users.set([]);
    this.competencies.set([]);
    this.tags.set([]);
    this.platforms.set([]);
  }

  getCountries() {
    this.eventService.getCountry().subscribe((res: any) => {
      if (res.length === 1) {
        this.getCantons(res[0].id);
        this.eventModel.update(e => ({...e, countryId: res[0].id}));
      } else {
        this.countries.set(res);
      }
    });
  }

  getCantons(id) {
    this.eventService.getCity(id).subscribe((res: any) => {
      const data = this.eventData();
      if (res.length === 1) {
        this.getOrganizations(res[0].id);
        this.eventModel.update(e => ({...e, cityId: res[0].id}));
      } else {
        this.cities.set(res);
      }
      if (data?.cityId && res.find(c => c.id === data.cityId)) {
        this.eventModel.update(e => ({...e, cityId: data?.cityId}));
        this.getOrganizations(data.cityId);
      }
    });
  }

  getOrganizations(id) {
    this.eventService.getOrganizationByCity(id).subscribe((res: any) => {
      const data = this.eventData();
      if (res.length === 1) {
        this.eventModel.update(e => ({...e, organizationId: res[0].id}));
      } else {
        this.organizations.set(res);
      }
      if (data?.organizationId && res.find(c => c.id === data.organizationId)) {
        this.eventModel.update(e => ({...e, organizationId: data?.organizationId}));
      }
    });
  }

  getEventTypes() {
    this.eventService.getEventType().subscribe((res: any) => {
      this.eventTypes.set(res);
    });
  }

  getUserByOrganization(id) {
    const data = this.eventData();
    this.eventService.getUserByOrganization(id).subscribe((res: any) => {
      this.users.set(res);
      if (data?.userId && res.find(c => c.id === data.userId)) {
        this.eventModel.update(e => ({...e, userId: data?.userId}));
      }
    });
  }

  getCompetenciesByOrganization(id) {
    const data = this.eventData();
    this.competencyService.competenciesByOrganization(id).subscribe((res: any) => {
      this.competencies.set(res);
      if (data?.skills) {
        this.eventModel.update(e => ({...e, skills: data?.skills}));
      }
    });
  }

  getTagsByOrganization(id) {
    const data = this.eventData();
    this.eventService.getTagByOrganization(id).subscribe((res: any) => {
      this.tags.set(res);
      if (data?.tags) {
        this.eventModel.update(e => ({...e, tags: data?.tags}));
      }
    });
  }

  getPlatformsByOrganization(id) {
    const data = this.eventData();
    this.platformsService.getByOrganizationId(id).subscribe((res: any) => {
      this.platforms.set(res);
      if (res.length === 1) {
        this.eventModel.update(e => ({...e, platforms: [res[0].id]}));
      }
      if (data?.platforms) {
        this.eventModel.update(e => ({...e, platforms: data?.platforms}));
      }
    });
  }

  getTargetGroups() {
    this.spinner.show();
    this.eventService.targetGroupsGroped(false).subscribe((res: any) => {
      this.targetGroups.set(res.targetGroups);
      this.spinner.hide();
    });
  }

  getEventLevels() {
    this.eventService.getLevels().subscribe((res: any) => {
      this.eventLevels.set(res);
    });
  }

  toggleSelectAll(event: any) {
    const allIds = event.checked ? this.allAvailableIds() : [];
    this.eventModel.update(prev => ({...prev, targetGroup: allIds}));
  }

  isGroupSelected(group: any) {
    const selected = this.eventModel().targetGroup || [];
    return group.data.every(item => selected.includes(item.id));
  }

  isGroupPartiallySelected(group: any) {
    const selected = this.eventModel().targetGroup || [];
    const inGroup = group.data.filter(item => selected.includes(item.id));
    return inGroup.length > 0 && inGroup.length < group.data.length;
  }

  toggleGroupSelection(event: any, group: any) {
    const currentSelected = [...(this.eventModel().targetGroup || [])];
    const groupIds = group.data.map(i => i.id);

    const newSelected = event.checked
      ? [...new Set([...currentSelected, ...groupIds])]
      : currentSelected.filter(id => !groupIds.includes(id));

    this.eventModel.update(prev => ({...prev, targetGroup: newSelected}));
  }

  removeDate(index: number) {
    this.eventModel.update(prev => ({...prev, dates: prev.dates.filter((_, i) => i !== index)}));
  }

  addDate() {
    this.eventModel.update(prev => {
      const firstDateEntry = prev.dates[0];

      const newStartTime = new Date();
      if (firstDateEntry?.startTime) {
        newStartTime.setHours(
          firstDateEntry.startTime.getHours(),
          firstDateEntry.startTime.getMinutes(),
          0, 0
        );
      } else {
        newStartTime.setHours(9, 0, 0, 0);
      }

      const newEndTime = new Date();
      if (firstDateEntry?.endTime instanceof Date) {
        newEndTime.setHours(
          firstDateEntry.endTime.getHours(),
          firstDateEntry.endTime.getMinutes(),
          0, 0
        );
      } else {
        newEndTime.setHours(18, 0, 0, 0);
      }
      return {...prev, dates: [...prev.dates, {date: new Date(), startTime: newStartTime, endTime: newEndTime}]};
    });
  }

  addWeblink(event: MatChipInputEvent): void {
    const value = (event.value || '').trim();
    if (value) {
      this.eventModel.update(prev => ({
        ...prev,
        weblinks: [...prev.weblinks, value]
      }));
    }
    event.chipInput?.clear();
  }

  onDescriptionChange(event: any) {
    if (event.source !== 'user') { return; }
    this.eventModel.update(prev => ({
      ...prev,
      description: event.html
    }));
  }

  addDocuments(e, type?: string) {
    const doc = e[e.length - 1];
    const documentWithType = type ? {file: doc?.file, type, tempId: doc?.tempId} : {file: doc?.file, tempId: doc?.tempId};
    this.eventModel.update(prev => ({...prev, files: [...prev.files, documentWithType]}));
  }

  setFilesAccess(e) {
    this.eventModel.update(prev => ({...prev, files: prev.files.map(f => f.tempId === e.tempId ? {...f, access: e.checked.toString()} : f)}));
  }

  handleFileDeleted(file) {
    this.eventData().documents = this.eventData().documents.filter(el => el.id !== file.id);
  }

  handleNewFileDeleted(tempId) {
    this.eventModel.update(prev => ({...prev, files: prev.files.filter(f => f.tempId !== tempId)}));
  }

  removeWeblink(link: string): void {
    this.eventModel.update(prev => ({
      ...prev,
      weblinks: prev.weblinks.filter(l => l !== link)
    }));
  }

  addSubEvent() {
    this.eventModel.update(prev => ({...prev, subEventsData: [...prev.subEventsData,
        {
          title: '',
          description: '',
          date: new Date(),
          fromTime: new Date(),
          toTime: new Date(),
          minParticipants: 0,
          maxParticipants: 0,
          picture: '',
          visibility: 'public',
          file: null,
          skills: []
        }
        ]}));
  }

  removeSubEvent(index: number) {
    this.eventModel.update(prev => ({...prev, subEventsData: prev.subEventsData.filter((_, i) => i !== index)}));
  }

  getImg(e, index) {
    this.eventModel.update(prev => ({...prev,
      subEventsData: prev.subEventsData.map((item, i) => i === index ? {...item, file: e} : item)}));
  }

  getMainImage(e ) {
    this.eventModel.update(prev => ({...prev, pictureFile: e}));
  }

  openCropper(e) {
    this.opennedCropper.set(e);
  }

  updateLocation(e) {
    this.eventModel.update(prev => ({...prev, location: e}));
  }

  getImage(name) {
    return this.eventService.getFileStream(name);
  }

  cleanDateField(field) {
    this.eventModel.update(prev => ({...prev, [field]: null}));
  }

  editGuestSetting() {
    let dialogRef = this.dialog.open(GuestEventComponent, {
      panelClass: 'guest-form-dialog',
      width: '70vw',
      data: {
        eventData: this.eventModel(),
        disableAll: this.disableAll()
      }
    });

    dialogRef.afterClosed()
      .subscribe((response) => {
        if (response) {
          if (response && response.guestIndividual) {
            response.guestIndividual = JSON.stringify(response.guestIndividual.map(el => {
              if (el.approve && el.value) {
                return el;
              }
            }).filter(el => !!el));
          }
          console.log(response);
          this.eventModel.update(prev => ({
            ...prev,
            ...response
          }));
        }
      });
  }

  getSelectedNeededLevelsDisplay(): string {
    const selectedIds = this.eventForm.needEventLevel().value();
    if (!selectedIds || selectedIds.length === 0) { return ''; }

    const selectedLevels = this.eventLevels()
      .filter(el => selectedIds.includes(el.id))
      .map(el => `${el.level} ${el.status ? el.status : ''}`);

    return selectedLevels.join(', ');
  }

  getSelectedEventLevelsDisplay(): string {
    const selectedIds = this.eventForm.eventLevel().value();
    if (!selectedIds || selectedIds.length === 0) {
      return '';
    }

    const selectedLevels = this.eventLevels()
      .filter(el => selectedIds.includes(el.id))
      .map(el => `${el.level} ${el.status ? el.status : ''}`);

    return selectedLevels.join(', ');
  }

  getSupportRequirements() {
    if (!this.eventData()?.documents) {
      return [];
    }
    return this.eventData().documents.filter(doc => doc.type === 'supportRequirements').map(doc => {
      return {
        ...doc,
        fileType: doc.name.split('.').pop()
      };
    });
  }

  getConditionsOfParticipation() {
    if (!this.eventData()?.documents) {
      return [];
    }
    return this.eventData().documents.filter(doc => doc.type === 'conditionsOfParticipation').map(doc => {
      return {
        ...doc,
        fileType: doc.name.split('.').pop()
      };
    });
  }

  getEventDocuments() {
    if (!this.eventData()?.documents) {
      return [];
    }
    return this.eventData().documents.filter(doc => doc.type === 'event').map(doc => {
      return {
        ...doc,
        fileType: doc.name.split('.').pop()
      };
    });
  }

  onRangePickerClosed() {
    const dates = this.eventModel().dates;
    const startDate = dates?.[0]?.date;
    const endDate = dates?.[1]?.date;

    if (startDate && !endDate) {
      this.eventModel.update(prev => ({
        ...prev,
        dates: prev.dates.map((d, i) =>
          i === 1 ? {...d, date: new Date(startDate)} : d
        )
      }));
    }
  }

  onChatFormResult(e) {
    this.eventModel.update(prev => ({...prev, chatSetting: e}));
  }

  onCertificateFormResult(e: CertificateSetting) {
    this.eventModel.update(prev => ({...prev, ...e}));
  }
}
